import { collection, addDoc, getDocs, doc, getDoc, updateDoc, deleteDoc, query, where, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { EventPhoto, PhotoPurchase } from '../types';
import { v4 as uuidv4 } from 'uuid';

const photosCollection = collection(db, 'eventPhotos');
const purchasesCollection = collection(db, 'photoPurchases');

// Photo pricing configuration
export const PHOTO_PRICING = {
  basePrice: 15, // R$ 15 por foto
  discount3Plus: 0.10, // 10% desconto para 3+ fotos
  discount5Plus: 0.30, // 30% desconto para 5+ fotos
};

// Calculate photo purchase total with discounts
export const calculatePhotoTotal = (photoCount: number, basePrice: number = PHOTO_PRICING.basePrice) => {
  const subtotal = photoCount * basePrice;
  let discount = 0;
  
  if (photoCount >= 5) {
    discount = PHOTO_PRICING.discount5Plus;
  } else if (photoCount >= 3) {
    discount = PHOTO_PRICING.discount3Plus;
  }
  
  const discountAmount = subtotal * discount;
  const total = subtotal - discountAmount;
  
  return {
    subtotal,
    discount: discount * 100, // Convert to percentage
    discountAmount,
    total
  };
};

// Add watermark to image (simplified - in production you'd use a proper image processing service)
const addWatermark = async (file: File): Promise<Blob> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Draw original image
      ctx.drawImage(img, 0, 0);
      
      // Create diagonal pattern watermark similar to the reference image
      const watermarkText = 'MEETTIX';
      const secondaryText = 'NÃO AUTORIZADO';
      const websiteText = 'www.meettix.com';
      
      // Calculate optimal font sizes based on image dimensions
      const baseFontSize = Math.max(16, Math.min(canvas.width, canvas.height) * 0.03);
      const mainFontSize = baseFontSize * 1.5;
      const secondaryFontSize = baseFontSize * 0.8;
      const smallFontSize = baseFontSize * 0.6;
      
      // Set up diagonal pattern
      const spacing = Math.max(120, Math.min(canvas.width, canvas.height) * 0.15);
      const diagonalSpacing = spacing * 1.4;
      
      // Save the current context
      ctx.save();
      
      // Rotate canvas for diagonal effect
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(-Math.PI / 6); // -30 degrees
      ctx.translate(-canvas.width / 2, -canvas.height / 2);
      
      // Create pattern that covers entire image
      const patternWidth = canvas.width * 1.5;
      const patternHeight = canvas.height * 1.5;
      const startX = -patternWidth * 0.25;
      const startY = -patternHeight * 0.25;
      
      // Draw diagonal pattern
      for (let x = startX; x < patternWidth; x += diagonalSpacing) {
        for (let y = startY; y < patternHeight; y += diagonalSpacing) {
          // Alternate between different watermark elements
          const patternIndex = Math.floor((x + y) / diagonalSpacing) % 3;
          
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          if (patternIndex === 0) {
            // Main MEETTIX watermark
            ctx.font = `bold ${mainFontSize}px Arial`;
            ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
            ctx.lineWidth = 2;
            
            ctx.strokeText(watermarkText, x, y);
            ctx.fillText(watermarkText, x, y);
            
            // Add secondary text below
            ctx.font = `bold ${secondaryFontSize}px Arial`;
            ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
            ctx.lineWidth = 1;
            
            ctx.strokeText(secondaryText, x, y + mainFontSize + 5);
            ctx.fillText(secondaryText, x, y + mainFontSize + 5);
            
          } else if (patternIndex === 1) {
            // Website watermark
            ctx.font = `${secondaryFontSize}px Arial`;
            ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
            ctx.lineWidth = 1;
            
            ctx.strokeText(websiteText, x, y);
            ctx.fillText(websiteText, x, y);
            
          } else {
            // Smaller MEETTIX
            ctx.font = `bold ${smallFontSize}px Arial`;
            ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
            ctx.lineWidth = 1;
            
            ctx.strokeText(watermarkText, x, y);
            ctx.fillText(watermarkText, x, y);
          }
        }
      }
      
      // Restore context
      ctx.restore();
      
      // Add additional corner watermarks for extra protection
      ctx.font = `bold ${secondaryFontSize}px Arial`;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.lineWidth = 2;
      ctx.textAlign = 'center';
      
      // Center watermark (most prominent)
      ctx.strokeText(watermarkText, canvas.width / 2, canvas.height / 2);
      ctx.fillText(watermarkText, canvas.width / 2, canvas.height / 2);
      
      // Add copyright symbol and year
      ctx.font = `${smallFontSize}px Arial`;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.lineWidth = 1;
      
      const copyrightText = `© ${new Date().getFullYear()} MEETTIX`;
      ctx.strokeText(copyrightText, canvas.width / 2, canvas.height / 2 + secondaryFontSize + 10);
      ctx.fillText(copyrightText, canvas.width / 2, canvas.height / 2 + secondaryFontSize + 10);
      
      canvas.toBlob((blob) => {
        resolve(blob!);
      }, 'image/jpeg', 0.9);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

// Create thumbnail
const createThumbnail = async (file: File): Promise<Blob> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    const img = new Image();
    
    img.onload = () => {
      const maxSize = 300;
      const ratio = Math.min(maxSize / img.width, maxSize / img.height);
      
      canvas.width = img.width * ratio;
      canvas.height = img.height * ratio;
      
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob((blob) => {
        resolve(blob!);
      }, 'image/jpeg', 0.8);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

// Upload event photos (admin only)
export const uploadEventPhotos = async (
  eventId: string,
  files: File[],
  uploadedBy: string
): Promise<string[]> => {
  try {
    const uploadPromises = files.map(async (file) => {
      const photoId = uuidv4();
      const filename = `${photoId}-${file.name}`;
      
      // Create watermarked version
      const watermarkedBlob = await addWatermark(file);
      const thumbnailBlob = await createThumbnail(file);
      
      // Upload original (private)
      const originalRef = ref(storage, `event-photos/original/${eventId}/${filename}`);
      await uploadBytes(originalRef, file);
      const originalUrl = await getDownloadURL(originalRef);
      
      // Upload watermarked (public)
      const watermarkedRef = ref(storage, `event-photos/watermarked/${eventId}/${filename}`);
      await uploadBytes(watermarkedRef, watermarkedBlob);
      const watermarkedUrl = await getDownloadURL(watermarkedRef);
      
      // Upload thumbnail
      const thumbnailRef = ref(storage, `event-photos/thumbnails/${eventId}/${filename}`);
      await uploadBytes(thumbnailRef, thumbnailBlob);
      const thumbnailUrl = await getDownloadURL(thumbnailRef);
      
      // Save photo metadata
      const photoData: Omit<EventPhoto, 'id'> = {
        eventId,
        originalUrl,
        watermarkedUrl,
        thumbnailUrl,
        filename,
        uploadedBy,
        uploadedAt: new Date(),
        price: PHOTO_PRICING.basePrice,
        active: true
      };
      
      const docRef = await addDoc(photosCollection, photoData);
      return docRef.id;
    });
    
    return await Promise.all(uploadPromises);
  } catch (error) {
    console.error('Error uploading photos:', error);
    throw error;
  }
};

// Get photos for an event
export const getEventPhotos = async (eventId: string): Promise<EventPhoto[]> => {
  try {
    // Use a simpler query to avoid composite index requirement
    // Filter by eventId only, then filter active photos in memory
    const q = query(
      photosCollection,
      where('eventId', '==', eventId)
    );
    
    const querySnapshot = await getDocs(q);
    const allPhotos = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      uploadedAt: doc.data().uploadedAt?.toDate() || new Date()
    } as EventPhoto));
    
    // Filter active photos and sort by upload date in memory
    return allPhotos
      .filter(photo => photo.active)
      .sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime());
  } catch (error) {
    console.error('Error getting event photos:', error);
    return [];
  }
};

// Purchase photos
export const purchasePhotos = async (
  userId: string,
  eventId: string,
  photoIds: string[]
): Promise<string> => {
  try {
    const pricing = calculatePhotoTotal(photoIds.length);
    
    const purchaseData: Omit<PhotoPurchase, 'id'> = {
      userId,
      eventId,
      photoIds,
      totalAmount: pricing.total,
      discount: pricing.discount,
      purchaseDate: new Date(),
      status: 'pending',
      paymentId: `temp_${Date.now()}` // Temporary payment ID
    };
    
    const docRef = await addDoc(purchasesCollection, purchaseData);
    
    // Simulate payment processing delay
    setTimeout(async () => {
      try {
        await updateDoc(docRef, { 
          status: 'completed',
          paymentId: `pay_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        });
      } catch (error) {
        console.error('Error updating payment status:', error);
      }
    }, 2000); // 2 second delay to simulate payment processing
    
    return docRef.id;
  } catch (error) {
    console.error('Error purchasing photos:', error);
    throw error;
  }
};

// Get user's purchased photos for an event
export const getUserPurchasedPhotos = async (
  userId: string,
  eventId: string
): Promise<string[]> => {
  try {
    const q = query(
      purchasesCollection,
      where('userId', '==', userId),
      where('eventId', '==', eventId),
      where('status', '==', 'completed')
    );
    
    const querySnapshot = await getDocs(q);
    const purchasedPhotoIds: string[] = [];
    
    querySnapshot.docs.forEach(doc => {
      const data = doc.data() as PhotoPurchase;
      purchasedPhotoIds.push(...data.photoIds);
    });
    
    return [...new Set(purchasedPhotoIds)]; // Remove duplicates
  } catch (error) {
    console.error('Error getting purchased photos:', error);
    return [];
  }
};

// Delete photo (admin only)
export const deleteEventPhoto = async (photoId: string): Promise<void> => {
  try {
    const photoDoc = await getDoc(doc(db, 'eventPhotos', photoId));
    if (!photoDoc.exists()) return;
    
    const photoData = photoDoc.data() as EventPhoto;
    
    // Delete from storage
    const originalRef = ref(storage, `event-photos/original/${photoData.eventId}/${photoData.filename}`);
    const watermarkedRef = ref(storage, `event-photos/watermarked/${photoData.eventId}/${photoData.filename}`);
    const thumbnailRef = ref(storage, `event-photos/thumbnails/${photoData.eventId}/${photoData.filename}`);
    
    await Promise.all([
      deleteObject(originalRef),
      deleteObject(watermarkedRef),
      deleteObject(thumbnailRef)
    ]);
    
    // Delete from database
    await deleteDoc(doc(db, 'eventPhotos', photoId));
  } catch (error) {
    console.error('Error deleting photo:', error);
    throw error;
  }
};

// Check if user has purchased a specific photo
export const hasUserPurchasedPhoto = async (
  userId: string,
  photoId: string
): Promise<boolean> => {
  try {
    const q = query(
      purchasesCollection,
      where('userId', '==', userId),
      where('status', '==', 'completed'),
      where('photoIds', 'array-contains', photoId)
    );
    
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking photo purchase:', error);
    return false;
  }
};

// Get purchase status for a purchase ID
export const getPurchaseStatus = async (purchaseId: string): Promise<PhotoPurchase | null> => {
  try {
    const purchaseDoc = await getDoc(doc(db, 'photoPurchases', purchaseId));
    if (purchaseDoc.exists()) {
      const data = purchaseDoc.data();
      return {
        id: purchaseDoc.id,
        ...data,
        purchaseDate: data.purchaseDate?.toDate() || new Date()
      } as PhotoPurchase;
    }
    return null;
  } catch (error) {
    console.error('Error getting purchase status:', error);
    return null;
  }
};